import _ from 'lodash'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import { Document, ObjectType } from '@/core/models/document.model'
import { useGetEstablishmentBackgroundsQuery } from '@/core/services/establishment'

import { useRequestHandler } from '@/hooks/useRequestHandler'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { selectDocumentCreateRequestTracker, selectDocumentDeleteRequestTracker } from '@/redux/reducers/document.reducer'
import { addDocument, deleteDocuments } from '@/redux/reducers/document.reducer'
import { skipToken } from '@reduxjs/toolkit/query'

import { AddBackgroundDialog } from '../components/AddBackgroundDialog'

type AddBackgroundContainerProps = {
  open: boolean
  onClose: () => void
}

export const AddBackgroundContainer = ({ open, onClose }: AddBackgroundContainerProps) => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const authUser = useAppSelector(selectAuthUser)
  const [isUploading, setIsUploading] = useState(false)

  const { data: backgrounds, refetch: refetchBackgrounds } = useGetEstablishmentBackgroundsQuery(authUser?.establishment?._id ? { establishmentId: authUser.establishment._id } : skipToken, {
    refetchOnMountOrArgChange: true,
  })

  const { dispatch: createRequestDispatch } = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.background.add.success'), { variant: 'success' })
      setIsUploading(false)
      refetchBackgrounds()
    },
    onError: () => {
      enqueueSnackbar(t('notification.background.add.error'), { variant: 'error' })
      setIsUploading(false)
    },
    requestTrackerSelector: selectDocumentCreateRequestTracker,
  })

  const { dispatch: deleteRequestDispatch, requestTracker: documentDeleteRequestTracker } = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.document.delete.success'), { variant: 'success' })
      refetchBackgrounds()
    },
    onError: () => {
      enqueueSnackbar(t('notification.document.delete.error'), { variant: 'error' })
    },
    requestTrackerSelector: selectDocumentDeleteRequestTracker,
  })

  const handleAddBackground = useCallback(
    (file: File) => {
      if (!file || !authUser?.establishment?._id) {
        enqueueSnackbar(t('error.file.required'), { variant: 'error' })
        return
      }

      setIsUploading(true)
      createRequestDispatch(
        addDocument({
          documentFile: file,
          objectType: 'establishment',
          objectId: authUser.establishment._id,
        })
      )
    },
    [authUser?.establishment?._id, createRequestDispatch, enqueueSnackbar, t]
  )

  const handleDeleteBackgrounds = useCallback(
    (documents: Document[]) => {
      if (!authUser?.establishment?._id || !documents.length) return

      const documentsToDelete = _.map(documents, (document) => ({
        filename: document.filename,
        key: document.key,
        objectType: 'establishment' as ObjectType,
      }))

      deleteRequestDispatch(deleteDocuments(documentsToDelete, 'establishment', authUser.establishment._id))
    },
    [authUser?.establishment?._id, deleteRequestDispatch]
  )

  return (
    <AddBackgroundDialog
      open={open}
      onClose={onClose}
      onAddBackground={handleAddBackground}
      isUploading={isUploading}
      backgrounds={backgrounds}
      onDelete={handleDeleteBackgrounds}
      isDeleting={documentDeleteRequestTracker.loading}
    />
  )
}

export default AddBackgroundContainer
